import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings2 } from 'lucide-react';

export interface PropertyFeatures {
  [category: string]: string[];
}

const FEATURE_CATEGORIES: { title: string; key: string; items: string[] }[] = [
  {
    title: 'Main Features',
    key: 'main',
    items: [
      'Central Air Conditioning', 'Central Heating', 'Double Glazed Windows',
      'Waste Disposal', 'Electricity Backup / Generator', 'Furnished',
      'Semi-Furnished', 'Marble / Tile Flooring', 'Wooden Flooring',
      'Built-in Wardrobes', 'Solar Panels', 'Water Tank / Cistern',
    ],
  },
  {
    title: 'Rooms',
    key: 'rooms',
    items: [
      'Drawing Room', 'Dining Room', 'Kitchen', 'Study Room / Office',
      'Prayer Room', 'Gymnasium / Gym', 'Store Room', 'Laundry Room',
      'Servant Quarter', 'Guest Room', 'Balcony',
    ],
  },
  {
    title: 'Business & Communication',
    key: 'business',
    items: [
      'Broadband Internet', 'Satellite / Cable TV', 'Intercom',
      'Conference Room', 'CCTV Security', 'Fire Alarm System',
    ],
  },
  {
    title: 'Healthcare & Recreational',
    key: 'recreation',
    items: [
      'Lawn / Garden', 'Swimming Pool', 'Sauna / Steam Room',
      'Jacuzzi', 'Kids Play Area', 'Barbecue Area',
      'Community Hall', 'Rooftop Terrace',
    ],
  },
  {
    title: 'Nearby Locations & Facilities',
    key: 'nearby',
    items: [
      'Near Schools', 'Near Hospitals', 'Near Shopping Malls',
      'Near Restaurants', 'Near Airport', 'Near Public Transport',
      'Near Parks', 'Near Places of Worship',
    ],
  },
  {
    title: 'Other Facilities',
    key: 'other',
    items: [
      'Maintenance Staff', 'Security Staff / Guard', 'Facilities for Disabled',
      'Elevator / Lift', 'Parking (Covered)', 'Parking (Uncovered)',
      'Pet Friendly', 'Gated Community',
    ],
  },
];

interface Props {
  features: PropertyFeatures;
  onChange: (features: PropertyFeatures) => void;
}

export function PropertyFeaturesDialog({ features, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<PropertyFeatures>(features);

  const handleOpen = () => {
    setLocal({ ...features });
    setOpen(true);
  };

  const toggle = (category: string, item: string) => {
    setLocal(prev => {
      const arr = prev[category] || [];
      return {
        ...prev,
        [category]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item],
      };
    });
  };

  const totalSelected = Object.values(local).reduce((s, a) => s + a.length, 0);
  const currentTotal = Object.values(features).reduce((s, a) => s + a.length, 0);

  return (
    <>
      <Button type="button" variant="outline" onClick={handleOpen} className="w-full justify-start gap-2">
        <Settings2 className="h-4 w-4" />
        Property Features {currentTotal > 0 && <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{currentTotal} selected</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Select Property Features ({totalSelected} selected)</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-6">
              {FEATURE_CATEGORIES.map(cat => (
                <div key={cat.key}>
                  <h4 className="font-semibold text-sm mb-3 text-primary">{cat.title}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {cat.items.map(item => {
                      const checked = (local[cat.key] || []).includes(item);
                      return (
                        <label key={item} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 rounded px-2 py-1.5">
                          <Checkbox checked={checked} onCheckedChange={() => toggle(cat.key, item)} />
                          <span>{item}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => { onChange(local); setOpen(false); }}>
              Save Features ({totalSelected})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
